import React, { useState } from "react";
import {
  DocumentIcon,
  PlusIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  PresentationChartBarIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";
import HoverActionsMenu from "../../../components/HoverActionsMenu";
import { VisibilityBadge } from "../../../activities/ActivityView";

function formatFileSize(
  bytes: number,
  { base = 1024, decimals = 1 }: { base?: 1000 | 1024; decimals?: number } = {}
): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes === 0) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(base));
  const value = bytes / Math.pow(base, i);
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  return `${value < 10 ? value.toFixed(decimals) : Math.round(value)} ${units[i]}`;
}

export type FileRow = {
  id: string | number;
  name: string;
  visibility?: "Shared" | "Private";
  size?: number | string;
  sizeType?: number | string;
  uploaderName?: string;
  uploaderAvatar?: string;
  timeLabel?: string;
};

export interface FilesSectionProps {
  title?: string;
  files: FileRow[];
  onUpload?: () => void | Promise<void>;
  onUploadSuccess?: () => void; // Callback after successful upload to refresh data
  onOpen?: (fileId: string | number) => void;
  onDelete?: (fileId: string | number) => void;
}

export function getIconByTypeOrName(file: FileRow) {
  const cls = "w-5 h-5 text-gray-500 shrink-0";
  const m = (file.sizeType ? String(file.sizeType) : "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() || "" : "";

  // Prefer MIME
  if (m.startsWith("image/")) return <PhotoIcon className={cls} />;
  if (m.startsWith("video/")) return <VideoCameraIcon className={cls} />;
  if (m.startsWith("audio/")) return <MusicalNoteIcon className={cls} />;
  if (m === "application/pdf") return <DocumentTextIcon className={cls} />;
  if (
    m === "application/zip" ||
    m === "application/x-zip-compressed" ||
    m === "application/x-rar-compressed" ||
    m === "application/x-7z-compressed"
  )
    return <ArchiveBoxIcon className={cls} />;
  if (
    m === "application/vnd.ms-excel" ||
    m === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    m === "text/csv"
  )
    return <TableCellsIcon className={cls} />;
  if (
    m === "application/vnd.ms-powerpoint" ||
    m === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  )
    return <PresentationChartBarIcon className={cls} />;
  if (
    m === "application/msword" ||
    m === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    m.startsWith("text/")
  )
    return <DocumentTextIcon className={cls} />;

  // Fallback by extension
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext))
    return <PhotoIcon className={cls} />;
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))
    return <VideoCameraIcon className={cls} />;
  if (["mp3", "wav", "aac", "flac", "m4a"].includes(ext))
    return <MusicalNoteIcon className={cls} />;
  if (["zip", "rar", "7z"].includes(ext)) return <ArchiveBoxIcon className={cls} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <TableCellsIcon className={cls} />;
  if (["ppt", "pptx", "key"].includes(ext)) return <PresentationChartBarIcon className={cls} />;
  if (["doc", "docx", "pdf", "txt", "rtf", "md"].includes(ext))
    return <DocumentTextIcon className={cls} />;

  return <DocumentIcon className={cls} />;
}

export function FileRowComponent({
  file: f,
  onOpen,
  onDeleteClick,
  showTimeFirst = false,
}: {
  file: FileRow;
  onOpen?: (fileId: string | number) => void;
  onDeleteClick?: (fileId: string | number) => void;
  showTimeFirst?: boolean;
}) {
  return (
    <div className="relative group flex items-center py-2 gap-2 hover:bg-gray-50 transition-colors rounded-md">
      {/* Time - displayed at the front */}
      {showTimeFirst && f.timeLabel && (
        <div className="w-[150px] text-[10px] text-gray-500 shrink-0">{f.timeLabel}</div>
      )}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {getIconByTypeOrName(f)}
        <div
          className={`truncate text-sm font-medium text-gray-900 mr-3 ${
            onOpen ? "cursor-pointer hover:text-blue-600 transition-colors" : ""
          }`}
          title={f.name}
          onClick={() => onOpen?.(f.id)}
        >
          {f.name}
        </div>
      </div>
      <div className="flex items-center flex-1">
        <VisibilityBadge visibility={f.visibility} />
      </div>
      {f.size !== undefined && f.size !== null && (
        <div className="w-36 text-xs text-gray-600">
          {typeof f.size === "number" ? formatFileSize(f.size) : f.size}
        </div>
      )}
      <div className="w-72 flex items-center gap-2">
        {f.uploaderAvatar && <img src={f.uploaderAvatar} alt="" className="w-5 h-5 rounded-full" />}
        <div className="text-xs text-gray-900 truncate">{f.uploaderName}</div>
      </div>
      {!showTimeFirst && f.timeLabel && (
        <div className="w-40 text-xs text-gray-600">{f.timeLabel}</div>
      )}

      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={onOpen ? () => onOpen(f.id) : undefined}
        onDelete={onDeleteClick ? () => onDeleteClick(f.id) : undefined}
      />
    </div>
  );
}

export default function FilesSection({
  title = "Files",
  files,
  onUpload,
  onOpen,
  onDelete,
}: FilesSectionProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (fileId: string | number) => {
    setDeleteId(fileId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDelete) return;
    try {
      setDeleting(true);
      await onDelete(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch (error) {
      // Error handling can be added here if needed
      console.error("Failed to delete file:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="w-full flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start justify-between pb-2 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 text-xs font-medium text-gray-900"
          >
            <PlusIcon className="w-4 h-4" />
            Upload File
          </button>
        </div>

        {/* List */}
        <div className="flex flex-col divide-y divide-gray-100">
          {files.map((f) => (
            <FileRowComponent
              key={f.id}
              file={f}
              onOpen={onOpen}
              onDeleteClick={handleDeleteClick}
            />
          ))}
          {files.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-600">No files yet</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete file?")}
        description={t("This action cannot be undone. The file will be permanently deleted.")}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={handleConfirmDelete}
        submitBtnLabel={t("Delete")}
      />
    </>
  );
}
